import { ReactNode, createContext, useContext, useMemo } from "react";
import store from "../../redux/store";
import { clearModals } from "../modal/modalSlice";
import { RouteState, useRouteState } from "../../util/useRouteState";

export const STRIPE_RETURN_PATH_KEY = "stripeReturnPath";
export const POST_SIGNIN_RETURN_KEY = "postSigninReturnTo";

/** Stash where to send the user after sign-in (e.g. anonymous pricing-CTA flow). */
export function stashPostSigninReturn(tier: "supporter" | "pro"): void {
  try {
    sessionStorage.setItem(POST_SIGNIN_RETURN_KEY, `/pricing?highlight=${tier}&source=post_signin`);
  } catch {
    // private mode / strict CSP — silent fail; user will land on / and have to navigate back.
  }
}

export interface Route {
  route: "home" | "board" | "session" | "privacy" | "pricing" | "templates" | "templateDetail" | "terms" | "blog" | "blogPost" | "trySession",
  boardUid: string | undefined,
  sessionUid: string | undefined,
  templateId: string | undefined,
  slug: string | undefined,
  from: string | undefined,
  encodedState: string | undefined,
  pathBuilder: () => string,
}

export interface HomeRoute extends Route {
  route: "home",
}
export function isHomeRoute(route: Route): route is HomeRoute { return route.route === "home" }

export interface BoardRoute extends Route {
  route: "board",
  boardUid: string,
}
export function isBoardRoute(route: Route): route is BoardRoute { return route.route === "board" }

export interface SessionRoute extends Route {
  route: "session",
  boardUid: string,
  sessionUid: string,
}
export function isSessionRoute(route: Route): route is SessionRoute { return route.route === "session" }

export interface PrivacyRoute extends Route {
  route: "privacy",
}
export function isPrivacyRoute(route: Route): route is PrivacyRoute { return route.route === "privacy" }

export interface PricingRoute extends Route {
  route: "pricing",
}
export function isPricingRoute(route: Route): route is PricingRoute { return route.route === "pricing" }

export interface TermsRoute extends Route {
  route: "terms",
}
export function isTermsRoute(route: Route): route is TermsRoute { return route.route === "terms" }

export interface TemplatesRoute extends Route {
  route: "templates",
}
export function isTemplatesRoute(route: Route): route is TemplatesRoute { return route.route === "templates" }

export interface TemplateDetailRoute extends Route {
  route: "templateDetail",
  templateId: string,
  from: "templates" | undefined,
}
export function isTemplateDetailRoute(route: Route): route is TemplateDetailRoute { return route.route === "templateDetail" }

export interface EphemeralSessionRoute extends Route {
  route: "trySession",
  templateId: string,
  encodedState: string | undefined,
}
export function isEphemeralSessionRoute(route: Route): route is EphemeralSessionRoute { return route.route === "trySession" }

export interface BlogRoute extends Route {
  route: "blog",
}
export function isBlogRoute(route: Route): route is BlogRoute { return route.route === "blog" }

export interface BlogPostRoute extends Route {
  route: "blogPost",
  slug: string,
}
export function isBlogPostRoute(route: Route): route is BlogPostRoute { return route.route === "blogPost" }

export const homeRoute: HomeRoute = {
  route: "home",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/",
};

export const privacyRoute: PrivacyRoute = {
  route: "privacy",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/privacy",
};

export const pricingRoute: PricingRoute = {
  route: "pricing",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/pricing",
};

export const pricingRouteHighlight = (
  tier: "supporter" | "pro",
  source: string,
): PricingRoute => ({
  route: "pricing",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => `/pricing?highlight=${tier}&source=${source}`,
});

export const termsRoute: TermsRoute = {
  route: "terms",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/terms",
};

export const templatesRoute: TemplatesRoute = {
  route: "templates",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/templates",
};

export const templatesRouteFromSession = (boardUid: string, sessionUid: string): TemplatesRoute => ({
  route: "templates",
  boardUid,
  sessionUid,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/templates",
});

// In-memory-state origin, mirroring templateDetailRoute's `from` param.
// The `from` rides on the route object (not the URL) — same pattern as
// templatesRouteFromSession. Refresh clears it; that's intentional.
export const templatesRouteWithFrom = (from: string): TemplatesRoute => ({
  route: "templates",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from,
  encodedState: undefined,
  pathBuilder: () => `/templates`,
});

export const templateDetailRoute = (
  templateId: string,
  from?: "templates",
): TemplateDetailRoute => ({
  route: "templateDetail",
  boardUid: undefined,
  sessionUid: undefined,
  templateId,
  slug: undefined,
  from,
  encodedState: undefined,
  // `from` rides on the route object (in-memory) for the back-link label —
  // deliberately NOT encoded in the URL. Same mental model as
  // templatesRouteFromSession: origin is transient navigation context, not
  // shareable content. Refresh clears it, which is the honest UX.
  pathBuilder: () => `/templates/${templateId}`,
});

export const blogRoute: BlogRoute = {
  route: "blog",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug: undefined,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => "/blog",
};

export const blogPostRoute = (slug: string): BlogPostRoute => ({
  route: "blogPost",
  boardUid: undefined,
  sessionUid: undefined,
  templateId: undefined,
  slug,
  from: undefined,
  encodedState: undefined,
  pathBuilder: () => `/blog/${slug}`,
});

export const boardRoute: (boardUid: string) => BoardRoute = (boardUid: string) => {
  return {
    route: "board",
    boardUid: boardUid,
    sessionUid: undefined,
    templateId: undefined,
    slug: undefined,
    from: undefined,
    encodedState: undefined,
    pathBuilder: () => `/board/${boardUid}`,
  }
}

export const sessionRoute: (boardUid: string, sessionUid: string) => SessionRoute = (boardUid: string, sessionUid: string) => {
  return {
    route: "session",
    boardUid: boardUid,
    sessionUid: sessionUid,
    templateId: undefined,
    slug: undefined,
    from: undefined,
    encodedState: undefined,
    pathBuilder: () => `/board/${boardUid}/session/${sessionUid}`,
  }
}

// URL-encoded ephemeral session. `encodedState` (lz-string encoded) rides in the
// URL path so refresh survives and links share the snapshot. Empty state means
// "hydrate from the template starter columns".
export const trySessionRoute = (templateId: string, encodedState?: string): EphemeralSessionRoute => ({
  route: "trySession",
  boardUid: undefined,
  sessionUid: undefined,
  templateId,
  slug: undefined,
  from: undefined,
  encodedState,
  pathBuilder: () => encodedState ? `/try/${templateId}/${encodedState}` : `/try/${templateId}`,
});

export type AppRoute = HomeRoute | BoardRoute | SessionRoute | PrivacyRoute | PricingRoute | TemplatesRoute | TemplateDetailRoute | EphemeralSessionRoute | TermsRoute | BlogRoute | BlogPostRoute;

const defaultGoTo = (_goto: AppRoute) => { }

export const RouteContext = createContext<[AppRoute, (goto: AppRoute) => void]>([homeRoute, defaultGoTo]);
export const useRouteContext = () => useContext(RouteContext);

const routeStateToRoute: (routeState: RouteState) => AppRoute = (routeState: RouteState) => {
  if (routeState.page === "privacy") return privacyRoute;
  if (routeState.page === "terms") return termsRoute;
  if (routeState.page === "pricing") return pricingRoute;
  if (routeState.page === "blogPost" && routeState.slug) {
    return blogPostRoute(routeState.slug);
  }
  if (routeState.page === "blog") return blogRoute;
  if (routeState.page === "templateDetail" && routeState.templateId) {
    const from = routeState.from === "templates" ? "templates" : undefined;
    return templateDetailRoute(routeState.templateId, from);
  }
  if (routeState.page === "trySession" && routeState.templateId) {
    return trySessionRoute(routeState.templateId, routeState.encodedState);
  }
  if (routeState.page === "templates") {
    if (routeState.boardUid && routeState.sessionUid) {
      return templatesRouteFromSession(routeState.boardUid, routeState.sessionUid);
    }
    if (routeState.from) {
      return templatesRouteWithFrom(routeState.from);
    }
    return templatesRoute;
  }
  return routeState.boardUid
    ? routeState.sessionUid
      ? sessionRoute(routeState.boardUid, routeState.sessionUid)
      : boardRoute(routeState.boardUid)
    : homeRoute
}

export const RouteProvider = ({ children }: { children: ReactNode }) => {
  const [routeState, setRouteState] = useRouteState()
  const route = routeStateToRoute(routeState)

  const goToRoute = useMemo(() => {
    return (next: AppRoute) => {
      // If we're navigating into /pricing from somewhere else, remember where the user came
      // from so we can drop them back there after a successful Stripe redirect.
      if (next.route === "pricing" && routeState.page !== "pricing") {
        try {
          sessionStorage.setItem(STRIPE_RETURN_PATH_KEY, window.location.pathname + window.location.search);
        } catch {
          // sessionStorage can throw in private modes / strict CSP — failure is non-fatal.
        }
      }

      // Modals shouldn't survive a navigation. RouteProvider sits outside the
      // Redux <Provider>, so dispatch via the store singleton rather than useDispatch.
      store.dispatch(clearModals())

      setRouteState({
        path: next.pathBuilder(),
        page: next.route === "privacy" ? "privacy"
          : next.route === "terms" ? "terms"
          : next.route === "pricing" ? "pricing"
          : next.route === "templateDetail" ? "templateDetail"
          : next.route === "templates" ? "templates"
          : next.route === "blog" ? "blog"
          : next.route === "blogPost" ? "blogPost"
          : next.route === "trySession" ? "trySession"
          : "home",
        boardUid: next.boardUid,
        sessionUid: next.sessionUid,
        templateId: next.templateId,
        slug: next.slug,
        from: next.from,
        encodedState: next.encodedState,
      })
    }
  }, [routeState.page, setRouteState])

  return (
    <RouteContext.Provider value={[route, goToRoute]}>
      {children}
    </RouteContext.Provider>
  );
};
